/**
 * The chart primitive — CLAUDE.md §4.5. The shadcn chart wrapper, with two departures forced by
 * §4.8: no injected per-chart `<style>` block (a series names a token, `chartColor()` turns it
 * into the `var(--…)` Recharts wants) and no inline `style` colours (swatches use static Tailwind
 * classes from the same closed token union).
 *
 * Leave `isAnimationActive` unset — Recharts defaults to `"auto"`, which honours
 * `prefers-reduced-motion` (§4.9); passing `true` opts a report out of the accessibility floor.
 *
 * ```tsx
 * const config = { efficiency: { label: 'Efficiency %', color: 'chart-1' } } satisfies ChartConfig;
 *
 * <ChartContainer config={config} aria-label="Efficiency by sewing line">
 *   <BarChart data={rows} accessibilityLayer>
 *     <ChartTooltip content={<ChartTooltipContent />} />
 *     <Bar dataKey="efficiency" radius={4}>
 *       {chartCells(rows, (row) => (row.belowTarget ? 'status-critical' : 'chart-1'))}
 *     </Bar>
 *   </BarChart>
 * </ChartContainer>
 * ```
 *
 * Why it is shaped the way it is: `src/components/design-notes.md`.
 */

import * as React from "react"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  type DefaultLegendContentProps,
  type LegendPayload,
  type TooltipContentProps,
  type TooltipPayload,
  type TooltipPayloadEntry,
  type TooltipValueType,
} from "recharts"

import { cn } from "@/lib/utils"

/**
 * Every colour a chart may use: the Tailwind class that paints a swatch, and the custom property
 * a Recharts mark is painted with. Static so Tailwind emits the classes, and a closed union so a
 * report cannot reach for a hex or a token that does not survive dark mode (§4.8).
 *
 * The `var()` names are raw tokens, not Tailwind `--color-*` theme names: `index.css` declares its
 * palette under `@theme inline`, which substitutes those at build time and never emits them.
 *
 * `chart-1`…`chart-5` are series identity, assigned in order and never cycled. The status colours
 * are colours only — a per-datum colour follows a flag the server returned (§5.2) and nothing here
 * decides what a flag means. `primary` is absent: it is reserved for actions, links and focus.
 */
const CHART_COLORS = {
  "chart-1": { swatch: "bg-chart-1", value: "var(--chart-1)" },
  "chart-2": { swatch: "bg-chart-2", value: "var(--chart-2)" },
  "chart-3": { swatch: "bg-chart-3", value: "var(--chart-3)" },
  "chart-4": { swatch: "bg-chart-4", value: "var(--chart-4)" },
  "chart-5": { swatch: "bg-chart-5", value: "var(--chart-5)" },
  "status-ok": { swatch: "bg-status-ok", value: "var(--status-ok)" },
  "status-warning": { swatch: "bg-status-warning", value: "var(--status-warning)" },
  "status-critical": { swatch: "bg-status-critical", value: "var(--status-critical)" },
  "status-neutral": { swatch: "bg-status-neutral", value: "var(--status-neutral)" },
  foreground: { swatch: "bg-foreground", value: "var(--foreground)" },
  "muted-foreground": { swatch: "bg-muted-foreground", value: "var(--muted-foreground)" },
  // The surface colour, for marks that separate themselves from what is behind them: the ring
  // around an active dot, the hairline gap between adjacent fills.
  card: { swatch: "bg-card", value: "var(--card)" },
} as const

export type ChartColorToken = keyof typeof CHART_COLORS

/**
 * The value for any Recharts colour prop — `fill`, `stroke`, `cursor.fill`. A `var()` reference
 * rather than a resolved colour, so the mark re-paints on a theme change; Recharts puts it on an
 * SVG presentation attribute, which is why this is not the inline `style` §4.8 rules out.
 */
function chartColor(token: ChartColorToken): string {
  return CHART_COLORS[token].value
}

/** How a series is drawn in the legend and tooltip, when it has no icon. */
export type ChartIndicator = "dot" | "line"

export interface ChartSeries {
  /** Named the way factory users say it (§4.10) — this is what the tooltip and legend show. */
  label: React.ReactNode
  /** Omit for a series that should not appear with a swatch (a reference band, say). */
  color?: ChartColorToken
  /** FontAwesome only (§4.13). Replaces the swatch when set. */
  icon?: IconDefinition
}

/**
 * Series key → how to present it. The key is the Recharts `dataKey`/`name`, so the tooltip and
 * legend can label a series without either of them being told twice.
 */
export type ChartConfig = Record<string, ChartSeries>

interface ChartContextValue {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextValue | null>(null)

function useChart(): ChartContextValue {
  const context = React.useContext(ChartContext)
  if (context === null) {
    throw new Error("useChart must be used inside a <ChartContainer>.")
  }
  return context
}

/**
 * Recharts draws axes, grid and cursor with hardcoded greys; these re-point them at theme tokens
 * for the whole subtree. The selectors are Recharts' own class names as of 3.10.1 — tick text is
 * `.recharts-cartesian-axis-tick-value` and is *not* a descendant of `…-tick`.
 */
const CHART_THEME = [
  "[&_.recharts-cartesian-axis-line]:stroke-border",
  "[&_.recharts-cartesian-axis-tick-line]:stroke-border",
  "[&_.recharts-cartesian-axis-tick-value]:fill-muted-foreground",
  "[&_.recharts-cartesian-axis-tick-value]:tabular-nums",
  "[&_.recharts-cartesian-grid_line]:stroke-border",
  "[&_.recharts-polar-grid_line]:stroke-border",
  "[&_.recharts-polar-angle-axis-line]:stroke-border",
  "[&_.recharts-polar-angle-axis-tick-line]:stroke-border",
  "[&_.recharts-polar-angle-axis-tick-value]:fill-muted-foreground",
  "[&_.recharts-polar-radius-axis-line]:stroke-border",
  "[&_.recharts-polar-radius-axis-tick-value]:fill-muted-foreground",
  "[&_.recharts-label]:fill-muted-foreground",
  "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border",
  "[&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted",
  // Recharts rings active dots and sectors in hardcoded white; in dark mode that is not the surface.
  "[&_.recharts-active-dot_.recharts-dot]:stroke-card",
  "[&_.recharts-sector]:stroke-card",
  // Bar radii and active dots sit slightly outside the plot area.
  "[&_.recharts-surface]:overflow-visible",
  // `accessibilityLayer` makes the plot keyboard-reachable; it has to show that it is (§4.9).
  "[&_svg]:focus-visible:outline-2",
  "[&_svg]:focus-visible:outline-ring",
].join(" ")

export type ChartContainerProps = Omit<
  React.ComponentProps<"div">,
  "aria-label" | "children"
> & {
  /** Required (§4.9): say what the chart shows — "Efficiency by sewing line". */
  "aria-label": string
  config: ChartConfig
  /** Exactly one Recharts chart element. */
  children: React.ReactElement
}

function ChartContainer({
  "aria-label": ariaLabel,
  config,
  className,
  children,
  ...props
}: ChartContainerProps) {
  const context = React.useMemo(() => ({ config }), [config])

  return (
    <ChartContext.Provider value={context}>
      <div
        data-slot="chart"
        // Not role="img": that would hide the plot's keyboard-reachable content from assistive tech.
        role="group"
        aria-roledescription="chart"
        aria-label={ariaLabel}
        // aspect-video rather than a fixed height, so the chart reflows to tablet width.
        className={cn("aspect-video w-full min-w-0 text-xs", CHART_THEME, className)}
        {...props}
      >
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
}

/**
 * Per-datum mark colours, where the server returned a flag and the colour follows it. Whether a
 * line is below target is decided in ADB SQL (§5.2); this only paints what the row already says,
 * and the flag → token mapping lives with the report, never as a threshold evaluated here.
 * Goes straight inside a series: `<Bar dataKey="x">{chartCells(rows, (r) => r.color)}</Bar>`.
 */
function chartCells<T>(
  data: readonly T[],
  color: (datum: T, index: number) => ChartColorToken,
): React.ReactElement[] {
  return data.map((datum, index) => (
    // Cells are matched to data points positionally, so the index is the identity.
    <Cell key={index} fill={chartColor(color(datum, index))} />
  ))
}

/**
 * Aliases rather than wrappers on purpose: Recharts positions these by finding them among the
 * chart's children, and a wrapper is not the element it looks for. Our changes are in `content`.
 */
const ChartTooltip = Tooltip
const ChartLegend = Legend

export type ChartTooltipContentProps = Partial<
  Pick<
    TooltipContentProps,
    "active" | "payload" | "label" | "labelFormatter" | "formatter"
  >
> & {
  className?: string
  /** Drop the header row — for a single-series chart where the axis already says it. */
  hideLabel?: boolean
  hideIndicator?: boolean
  indicator?: ChartIndicator
  /** Look the series up under this config key instead of the payload's name/dataKey. */
  nameKey?: string
  /** Take the header from this field of the hovered row instead of the axis label. */
  labelKey?: string
  /** Series keys to omit from the tooltip body while keeping the hovered datum as its heading. */
  hiddenSeries?: readonly string[]
  /** Series keys in their desired tooltip order; series not listed follow them. */
  seriesOrder?: readonly string[]
  /**
   * The swatch colour for one hovered mark, for a chart whose marks are individually filled:
   * `config` holds one colour per *series*, so without this the tooltip tells the reader a green
   * bar is blue. Return `undefined` to keep the configured colour. Takes the datum rather than the
   * Recharts payload entry so a caller reuses the function that painted the cell.
   */
  seriesColor?: (
    seriesKey: string | undefined,
    datum: unknown,
  ) => ChartColorToken | undefined
}

/**
 * The themed tooltip body. Pass it as an element — Recharts clones it with the hover payload:
 * `<ChartTooltip content={<ChartTooltipContent />} />`.
 */
function ChartTooltipContent({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
  className,
  hideLabel = false,
  hideIndicator = false,
  indicator = "dot",
  nameKey,
  labelKey,
  hiddenSeries,
  seriesOrder,
  seriesColor,
}: ChartTooltipContentProps) {
  const { config } = useChart()
  const visibleItems: TooltipPayload = (payload ?? []).filter((item) => {
    const key = tooltipSeriesKey(item, nameKey)
    return key === undefined || hiddenSeries?.includes(key) !== true
  })
  const items =
    seriesOrder === undefined
      ? visibleItems
      : [...visibleItems].sort((a, b) => {
          const position = (item: TooltipPayloadEntry) => {
            const index = seriesOrder.indexOf(tooltipSeriesKey(item, nameKey) ?? '')
            return index === -1 ? seriesOrder.length : index
          }
          return position(a) - position(b)
        })

  if (active !== true || items.length === 0) {
    return null
  }

  const first = items[0]
  const rawLabel: React.ReactNode =
    labelKey !== undefined && first !== undefined
      ? (readField(first, labelKey) ?? label)
      : label
  const namedLabel = typeof rawLabel === "string" ? config[rawLabel]?.label : undefined
  const heading =
    labelFormatter !== undefined
      ? labelFormatter(namedLabel ?? rawLabel, items)
      : (namedLabel ?? rawLabel)
  const showHeading = !hideLabel && heading !== undefined && heading !== null && heading !== ""

  return (
    <div
      data-slot="chart-tooltip-content"
      className={cn(
        "min-w-32 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md",
        className,
      )}
    >
      {showHeading ? <div className="mb-1 font-medium">{heading}</div> : null}
      <ul className="flex flex-col gap-1">
        {items.map((item, index) => {
          const key = tooltipSeriesKey(item, nameKey)
          const series = key === undefined ? undefined : config[key]

          return (
            <li key={`${key ?? "series"}-${index}`} className="flex items-center gap-1.5">
              {hideIndicator ? null : (
                <SeriesMark
                  series={series}
                  indicator={indicator}
                  color={seriesColor?.(key, item.payload)}
                />
              )}
              <span className="text-muted-foreground">{series?.label ?? item.name ?? key}</span>
              <span className="ml-auto pl-2 font-medium tabular-nums text-foreground">
                {/* The original `payload`, not `items`: `hiddenSeries`/`seriesOrder` filtered and
                    reordered that, and a formatter must not be lied to about its own data. */}
                {formatItemValue(item, formatter, payload ?? items)}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export type ChartLegendContentProps = Partial<
  Pick<DefaultLegendContentProps, "payload" | "verticalAlign">
> & {
  className?: string
  indicator?: ChartIndicator
  /** Look every series up under this config key instead of the payload's dataKey/value. */
  nameKey?: string
}

/**
 * The themed legend: `<ChartLegend content={<ChartLegendContent />} />`. Present it whenever a
 * chart has more than one series, so identity is never carried by colour alone (§4.9).
 */
function ChartLegendContent({
  payload,
  verticalAlign = "bottom",
  className,
  indicator = "dot",
  nameKey,
}: ChartLegendContentProps) {
  const { config } = useChart()
  const items = payload ?? []

  if (items.length === 0) {
    return null
  }

  return (
    <ul
      data-slot="chart-legend-content"
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground",
        verticalAlign === "top" ? "pb-2" : "pt-2",
        className,
      )}
    >
      {items.map((item, index) => {
        const key = legendSeriesKey(item, nameKey)
        const series = key === undefined ? undefined : config[key]

        return (
          <li key={`${key ?? "series"}-${index}`} className="flex items-center gap-1.5">
            <SeriesMark series={series} indicator={indicator} />
            <span>{series?.label ?? item.value}</span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The swatch beside a series name — its icon if it has one, otherwise a chip in its colour. The
 * class comes from `CHART_COLORS`, not the payload's raw colour string, which is what keeps the
 * colour out of an inline `style` (§4.8) and keeps chip and mark on the same token.
 */
function SeriesMark({
  series,
  indicator,
  color,
}: {
  series: ChartSeries | undefined
  indicator: ChartIndicator
  /** Overrides the series colour, for a chart that fills each mark individually. */
  color?: ChartColorToken
}) {
  if (series?.icon !== undefined) {
    return <FontAwesomeIcon icon={series.icon} className="size-3 shrink-0" aria-hidden />
  }

  // The override describes the mark under the pointer; the config describes the whole series.
  const token = color ?? series?.color
  if (token === undefined) {
    return null
  }

  return (
    <span
      aria-hidden
      className={cn(
        "shrink-0 rounded-full",
        indicator === "line" ? "h-1 w-3" : "size-2.5",
        CHART_COLORS[token].swatch,
      )}
    />
  )
}

/** The config key for a hovered mark. Recharts fills `name` from `name` or `dataKey`. */
function tooltipSeriesKey(
  item: TooltipPayloadEntry,
  nameKey: string | undefined,
): string | undefined {
  if (nameKey !== undefined) {
    return nameKey
  }
  if (typeof item.name === "string") {
    return item.name
  }
  if (typeof item.dataKey === "string") {
    return item.dataKey
  }
  return undefined
}

/** The config key for a legend entry. Here `dataKey` is the series and `value` is its text. */
function legendSeriesKey(item: LegendPayload, nameKey: string | undefined): string | undefined {
  if (nameKey !== undefined) {
    return nameKey
  }
  if (typeof item.dataKey === "string") {
    return item.dataKey
  }
  return item.value
}

/**
 * Read a field off the row behind a mark. Recharts types the datum as `any`; it goes through
 * `unknown` here so nothing downstream inherits that, and non-scalars are simply not shown.
 */
function readField(item: TooltipPayloadEntry, field: string): string | number | undefined {
  const datum: unknown = item.payload
  if (typeof datum !== "object" || datum === null) {
    return undefined
  }
  const value: unknown = (datum as Record<string, unknown>)[field]
  return typeof value === "string" || typeof value === "number" ? value : undefined
}

/**
 * The figure shown against a series. Formatting is the report's business, so this renders the
 * server's value as-is unless the caller supplied Recharts' `formatter`. `index` and `payload`
 * come from the **original** hover payload, not the filtered/reordered display list.
 */
function formatItemValue(
  item: TooltipPayloadEntry,
  formatter: ChartTooltipContentProps["formatter"],
  payload: TooltipPayload,
): React.ReactNode {
  if (formatter !== undefined && item.value !== undefined && item.name !== undefined) {
    const formatted = formatter(item.value, item.name, item, payload.indexOf(item), payload)
    // A formatter may return [value, name]; the name is already rendered beside it.
    if (Array.isArray(formatted)) {
      const [value] = formatted
      return value
    }
    return formatted
  }
  return displayValue(item.value)
}

function displayValue(value: TooltipValueType | undefined): React.ReactNode {
  if (value === undefined) {
    return null
  }
  if (typeof value === "string" || typeof value === "number") {
    return value
  }
  // A range (an area's [low, high], an error bar) arrives as a tuple.
  return value.join("–")
}

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  chartCells,
  chartColor,
  useChart,
}
